import React, { useState, useRef, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { Award, FileSpreadsheet, Download, CheckCircle, Loader2, Eye } from 'lucide-react';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';

// ── Template configs (same as Certificate.tsx) ─────────────────────────────
const TEMPLATES = [
  { name: 'Plantation Drive',            nameX: 0.50, nameY: 0.45, fontSize: 0.055, color: '#1a1a1a', fontStyle: 'bold' },
  { name: 'Beach Cleanup',               nameX: 0.50, nameY: 0.50, fontSize: 0.055, color: '#1a1a1a', fontStyle: 'bold' },
  { name: 'World Biggest Beach Cleanup', nameX: 0.50, nameY: 0.50, fontSize: 0.055, color: '#1a1a1a', fontStyle: 'bold' },
  { name: 'CPR & FIRST RESPONSE WORKSHOP', nameX: 0.50, nameY: 0.50, fontSize: 0.055, color: '#1a1a1a', fontStyle: 'bold' },
  { name: 'Fire Safety',                 nameX: 0.50, nameY: 0.50, fontSize: 0.055, color: '#1a1a1a', fontStyle: 'bold' },
  { name: 'Medical Camp',                nameX: 0.50, nameY: 0.50, fontSize: 0.055, color: '#1a1a1a', fontStyle: 'bold' },
  { name: 'Park Cleanup & Restoration', nameX: 0.50, nameY: 0.438, fontSize: 0.055, color: '#1a1a1a', fontStyle: 'bold' },
];

// ── Render certificate onto an off-screen canvas ───────────────────────────
function renderToCanvas(templateName: string, name: string): Promise<HTMLCanvasElement> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const cfg = TEMPLATES.find(t => t.name === templateName)!;
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      canvas.width  = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);

      const px = Math.round(img.naturalWidth * cfg.fontSize);
      ctx.font         = `${cfg.fontStyle} ${px}px Georgia, "Times New Roman", serif`;
      ctx.fillStyle    = cfg.color;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(name, img.naturalWidth * cfg.nameX, img.naturalHeight * cfg.nameY);
      resolve(canvas);
    };

    img.onerror = () => {
      // fallback blank canvas so download still works
      canvas.width = 1200; canvas.height = 850;
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 1200, 850);
      ctx.font = 'bold 60px Georgia, serif';
      ctx.fillStyle = '#1a1a1a'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(name, 600, 425);
      resolve(canvas);
    };

    img.src = `/Certificates/${templateName}/PNG.png`;
  });
}

// ── Generate PDF blob for one certificate ─────────────────────────────────
async function generatePDFBlob(templateName: string, name: string): Promise<ArrayBuffer> {
  const canvas = await renderToCanvas(templateName, name);
  const imgData = canvas.toDataURL('image/png');
  const isLandscape = canvas.width > canvas.height;

  const pdf = new jsPDF({
    orientation: isLandscape ? 'landscape' : 'portrait',
    unit: 'px',
    format: [canvas.width, canvas.height],
    hotfixes: ['px_scaling'],
  });

  pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
  return pdf.output('arraybuffer');
}

// ── Download one PDF directly ──────────────────────────────────────────────
async function downloadSinglePDF(templateName: string, name: string) {
  const bytes = await generatePDFBlob(templateName, name);
  const blob  = new Blob([bytes], { type: 'application/pdf' });
  const url   = URL.createObjectURL(blob);
  const a     = document.createElement('a');
  a.href      = url;
  a.download  = `${name} - ${templateName}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Parse CSV → first column names ────────────────────────────────────────
function parseNames(text: string): string[] {
  const lines = text.replace(/^﻿/, '').trim().split(/\r?\n/).filter(l => l.trim());
  if (!lines.length) return [];
  const firstCell = lines[0].split(',')[0].trim().replace(/^"|"$/g, '').toLowerCase();
  const isHeader  = ['name','names','full_name','fullname','full name','volunteer'].includes(firstCell);
  const names: string[] = [];
  for (let i = isHeader ? 1 : 0; i < lines.length; i++) {
    const n = lines[i].split(',')[0].trim().replace(/^"|"$/g, '').trim();
    if (n) names.push(n);
  }
  return names;
}

// ── Main page ──────────────────────────────────────────────────────────────
const CertificatesPage: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATES[0].name);
  const [names, setNames]           = useState<string[]>([]);
  const [csvFile, setCsvFile]       = useState<string>('');
  const [previewName, setPreviewName] = useState<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [zipProgress, setZipProgress] = useState(0); // 0–100
  const csvRef = useRef<HTMLInputElement>(null);

  const handleCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file.name);
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseNames(ev.target?.result as string);
      setNames(parsed);
      setPreviewName(parsed[0] ?? null);
    };
    reader.readAsText(file);
  };

  const showPreview = useCallback(async (name: string) => {
    setPreviewName(name);
    setPreviewLoading(true);
    const canvas = await renderToCanvas(selectedTemplate, name);
    setPreviewSrc(canvas.toDataURL('image/png'));
    setPreviewLoading(false);
  }, [selectedTemplate]);

  const handleDownloadOne = async (name: string) => {
    setDownloading(name);
    await downloadSinglePDF(selectedTemplate, name);
    setDownloading(null);
  };

  const handleDownloadAll = async () => {
    if (!names.length) return;
    setDownloadingAll(true);
    setZipProgress(0);

    const zip = new JSZip();

    for (let i = 0; i < names.length; i++) {
      const name  = names[i];
      const bytes = await generatePDFBlob(selectedTemplate, name);
      zip.file(`${name}.pdf`, bytes);
      setZipProgress(Math.round(((i + 1) / names.length) * 90));
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    setZipProgress(100);

    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href    = url;
    a.download = `${selectedTemplate} - Certificates.zip`;
    a.click();
    URL.revokeObjectURL(url);

    setDownloadingAll(false);
    setZipProgress(0);
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-secondary-50 pb-12">
        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* Header */}
          <div className="mb-8 flex items-center gap-3">
            <div className="p-2 bg-[#0D5128] rounded-xl"><Award size={24} className="text-white" /></div>
            <div>
              <h1 className="text-3xl font-bold text-secondary-900">Certificate Generator</h1>
              <p className="text-sm text-secondary-500 mt-0.5">Template select karo, CSV upload karo, PDF download karo</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ── Left panel ── */}
            <div className="space-y-5">

              {/* Template selector */}
              <div className="card p-5">
                <h2 className="text-xs font-semibold text-secondary-600 uppercase tracking-wide mb-3">Certificate Template</h2>
                <div className="space-y-2">
                  {TEMPLATES.map(t => (
                    <button
                      key={t.name}
                      onClick={() => { setSelectedTemplate(t.name); if (previewName) showPreview(previewName); }}
                      className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium border-2 transition-all ${
                        selectedTemplate === t.name
                          ? 'border-[#0D5128] bg-green-50 text-[#0D5128]'
                          : 'border-secondary-200 text-secondary-700 hover:border-[#0D5128]'
                      }`}
                    >
                      {selectedTemplate === t.name && <CheckCircle size={14} className="inline mr-2" />}
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* CSV Upload */}
              <div className="card p-5">
                <h2 className="text-xs font-semibold text-secondary-600 uppercase tracking-wide mb-3">Names CSV Upload</h2>
                <input ref={csvRef} type="file" accept=".csv" onChange={handleCsv} className="hidden" />
                <button
                  onClick={() => csvRef.current?.click()}
                  className={`w-full py-6 border-2 border-dashed rounded-xl flex flex-col items-center gap-2 transition-colors cursor-pointer ${
                    names.length > 0 ? 'border-[#0D5128] bg-green-50' : 'border-secondary-300 hover:border-[#0D5128]'
                  }`}
                >
                  {names.length > 0 ? (
                    <>
                      <CheckCircle size={22} className="text-[#0D5128]" />
                      <span className="text-sm font-semibold text-[#0D5128]">{names.length} names loaded</span>
                      <span className="text-xs text-secondary-400">{csvFile} — click to replace</span>
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet size={22} className="text-secondary-400" />
                      <span className="text-sm font-medium text-secondary-600">Upload CSV</span>
                      <span className="text-xs text-secondary-400">Pehla column = naam</span>
                    </>
                  )}
                </button>

                {/* CSV template download */}
                <a
                  href="data:text/csv;charset=utf-8,name%0AHamza%20Tariq%0AFatima%20Khan%0AAli%20Ahmed"
                  download="names_template.csv"
                  className="mt-2 block text-center text-xs text-[#0D5128] hover:underline"
                >
                  Sample CSV download karo
                </a>

                {/* Names preview list */}
                {names.length > 0 && (
                  <div className="mt-3 max-h-36 overflow-y-auto space-y-1">
                    {names.slice(0, 30).map((n, i) => (
                      <div key={i} className="text-xs text-secondary-600 px-2 py-1 bg-secondary-50 rounded flex items-center gap-1">
                        <span className="text-secondary-400 w-5">{i + 1}.</span> {n}
                      </div>
                    ))}
                    {names.length > 30 && (
                      <div className="text-xs text-secondary-400 px-2">...aur {names.length - 30} names</div>
                    )}
                  </div>
                )}
              </div>

              {/* Download All as ZIP */}
              {names.length > 0 && (
                <div className="space-y-2">
                  <button
                    onClick={handleDownloadAll}
                    disabled={downloadingAll}
                    className="w-full py-3 bg-[#0D5128] hover:bg-[#0a3e1f] disabled:opacity-60 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
                  >
                    {downloadingAll
                      ? <><Loader2 size={18} className="animate-spin" /> Generating... {zipProgress}%</>
                      : <><Download size={18} /> Download All as ZIP ({names.length})</>}
                  </button>
                  {downloadingAll && (
                    <div className="w-full bg-secondary-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-[#0D5128] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${zipProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Right panel: Preview + List ── */}
            <div className="lg:col-span-2 space-y-5">

              {/* Preview */}
              <div className="card p-5">
                <h2 className="text-xs font-semibold text-secondary-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Eye size={14} /> Preview
                  {previewName && <span className="font-normal normal-case text-secondary-400">— {previewName}</span>}
                </h2>
                <div className="rounded-xl overflow-hidden bg-secondary-100 flex items-center justify-center" style={{ minHeight: 240 }}>
                  {previewLoading ? (
                    <Loader2 size={28} className="animate-spin text-secondary-400" />
                  ) : previewSrc ? (
                    <img src={previewSrc} alt="Certificate preview" className="w-full h-auto rounded-xl" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-12 text-secondary-400">
                      <Award size={36} className="text-secondary-300" />
                      <p className="text-sm">CSV upload karo aur kisi naam pe click karo</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Names list with download buttons */}
              {names.length > 0 && (
                <div className="card overflow-hidden">
                  <div className="px-5 py-4 border-b border-secondary-100 flex items-center justify-between">
                    <h2 className="text-xs font-semibold text-secondary-600 uppercase tracking-wide">
                      Names List — {names.length} certificates
                    </h2>
                  </div>
                  <div className="divide-y divide-secondary-100 max-h-[480px] overflow-y-auto">
                    {names.map((name, i) => (
                      <div key={i} className="flex items-center justify-between px-5 py-3 hover:bg-secondary-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-secondary-400 w-6 text-right">{i + 1}</span>
                          <span className="text-sm font-medium text-secondary-800">{name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => showPreview(name)}
                            className="p-1.5 text-secondary-400 hover:text-[#0D5128] border-0 bg-transparent cursor-pointer rounded-lg hover:bg-green-50 transition-colors"
                            title="Preview"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleDownloadOne(name)}
                            disabled={downloading === name || downloadingAll}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0D5128] hover:bg-[#0a3e1f] disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
                          >
                            {downloading === name
                              ? <Loader2 size={13} className="animate-spin" />
                              : <Download size={13} />}
                            PDF
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CertificatesPage;
